/**
 * Display helpers for stock control. Pure formatting only: every rule
 * (re-order, projected, reasons) is decided by stock_module and arrives in
 * the API response.
 */
import type {
  StockAdjustmentReasons,
  StockBatchWithdrawalReason,
  StockHolding,
  StockLocationBalance,
  StockTransactionItem,
  StockTransactionType,
} from "../data/api-types";

/** Whether a history line put stock into its location. */
export function isInbound(type: StockTransactionType): boolean {
  return (
    type === "adjust_in" || type === "transfer_in" || type === "external_in"
  );
}

/** "Leeds 2,750 · Bristol 500" for the list's per-warehouse summary. */
export function formatLocationBalances(
  balances: StockLocationBalance[],
): string {
  return balances
    .map(
      (b) =>
        `${b.location_name ?? b.location_id} ${b.on_hand.toLocaleString("en-GB")}`,
    )
    .join(" · ");
}

/** Holdings at one location that have stock to move, oldest batch first
 * (holdings arrive from the backend in that order). */
export function holdingsWithStockAt(
  holdings: StockHolding[],
  locationId: string,
): StockHolding[] {
  return holdings.filter((h) => h.location_id === locationId && h.on_hand > 0);
}

/** Distinct locations where the item has stock, in the backend's order. */
export function locationsWithStock(
  holdings: StockHolding[],
): Pick<StockHolding, "location_id" | "location_name">[] {
  const seen = new Map<
    string,
    Pick<StockHolding, "location_id" | "location_name">
  >();
  for (const h of holdings) {
    if (h.on_hand > 0 && !seen.has(h.location_id)) {
      seen.set(h.location_id, {
        location_id: h.location_id,
        location_name: h.location_name,
      });
    }
  }
  return [...seen.values()];
}

/** A history line's movement with its sign: "+500" or "−30" (a true minus sign). */
export function formatSignedQuantity(
  transaction: Pick<StockTransactionItem, "quantity" | "transaction_type">,
): string {
  const sign = isInbound(transaction.transaction_type) ? "+" : "−";
  return `${sign}${transaction.quantity.toLocaleString("en-GB")}`;
}

/** Reason code -> label, from the backend's reason list (both directions, and the system's own). */
export function reasonLabelLookup(
  reasons: StockAdjustmentReasons | null,
): (reason: string) => string {
  const labels = new Map<string, string>();
  for (const option of [
    ...(reasons?.adjust_in ?? []),
    ...(reasons?.adjust_out ?? []),
    ...(reasons?.system ?? []),
  ]) {
    labels.set(option.reason, option.label);
  }
  return (reason) => labels.get(reason) ?? reason;
}

const WITHDRAWAL_LABELS: Record<StockBatchWithdrawalReason, string> = {
  revision_replaced: "Artwork revision replaced",
  revision_expired: "Artwork revision expired",
  by_hand: "Withdrawn by hand",
};

/** Why a batch was withdrawn, for the batches table. */
export function batchWithdrawalLabel(
  reason: StockBatchWithdrawalReason,
): string {
  return WITHDRAWAL_LABELS[reason];
}
